/**
 * Deploy Phase UI — overlay for planet selection during deploy phase.
 */

import { MODE_RULES } from '../constants';
import { fx } from './flavor';
import type { GameMode } from '../types';
import { theme } from './theme';

let overlayEl: HTMLDivElement | null = null;
let instructionEl: HTMLDivElement | null = null;
let confirmBtn: HTMLButtonElement | null = null;
let onConfirmCallback: (() => void) | null = null;
let modeRulesEl: HTMLDivElement | null = null;

export function showDeployOverlay(teamName: string, teamColor: string, mode?: GameMode): void {
  hideDeployOverlay();

  overlayEl = document.createElement('div');
  overlayEl.id = 'deploy-overlay';
  overlayEl.style.cssText = `
    position: fixed;
    top: 0; left: 0; right: 0;
    pointer-events: none;
    z-index: 100;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: 60px;
  `;

  instructionEl = document.createElement('div');
  instructionEl.style.cssText = `
    background: ${theme.surface.overlayDark};
    border: 1px solid ${teamColor};
    border-radius: 8px;
    padding: 16px 32px;
    color: ${theme.text.bright};
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
    font-size: 16px;
    text-align: center;
    pointer-events: auto;
    box-shadow: 0 0 20px ${teamColor}44;
  `;
  instructionEl.innerHTML = `
    <div style="color: ${teamColor}; font-size: 14px; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 2px;">Deploy Phase</div>
    <div>Select a planet for <span style="color: ${teamColor}; font-weight: bold;">${teamName}</span></div>
    <div style="font-size: 12px; color: ${theme.text.secondary}; margin-top: 8px;">Click a planet to select, then click a spot on its surface to confirm</div>
  `;
  overlayEl.appendChild(instructionEl);

  // Confirm button (initially hidden)
  confirmBtn = document.createElement('button');
  confirmBtn.textContent = 'CONFIRM DEPLOYMENT';
  confirmBtn.style.cssText = `
    display: none;
    margin-top: 16px;
    padding: 10px 24px;
    background: ${teamColor}33;
    border: 1px solid ${teamColor};
    border-radius: 4px;
    color: ${teamColor};
    font-family: 'JetBrains Mono', 'Fira Code', monospace;
    font-size: 14px;
    cursor: pointer;
    pointer-events: auto;
    text-transform: uppercase;
    letter-spacing: 1px;
    transition: background 0.2s;
  `;
  confirmBtn.addEventListener('mouseenter', () => {
    if (confirmBtn) confirmBtn.style.background = `${teamColor}55`;
  });
  confirmBtn.addEventListener('mouseleave', () => {
    if (confirmBtn) confirmBtn.style.background = `${teamColor}33`;
  });
  confirmBtn.addEventListener('click', () => {
    if (onConfirmCallback) onConfirmCallback();
  });
  overlayEl.appendChild(confirmBtn);

  // Mode rules section
  if (mode) {
    const info = MODE_RULES[mode];
    if (info) {
      modeRulesEl = document.createElement('div');
      modeRulesEl.style.cssText = `
        margin-top: 12px;
        background: ${theme.surface.overlay};
        border: 1px solid ${theme.text.disabled};
        border-radius: 6px;
        padding: 10px 20px;
        color: ${theme.text.secondary};
        font-family: 'JetBrains Mono', 'Fira Code', monospace;
        font-size: 11px;
        text-align: left;
        max-width: 400px;
        pointer-events: auto;
        cursor: pointer;
      `;
      const bullets = info.rules.map(r => `<div style="margin-left:8px;">&bull; ${r}</div>`).join('');
      const rulesContent = `<div style="margin-top:6px;display:none;" id="deploy-rules-body"><em>${info.objective}</em><br>${bullets}</div>`;
      modeRulesEl.innerHTML = `<div style="color:${theme.text.secondary};text-transform:uppercase;letter-spacing:1px;">Mission Briefing (click to expand)</div>${rulesContent}`;
      modeRulesEl.addEventListener('click', () => {
        const body = document.getElementById('deploy-rules-body');
        if (body) body.style.display = body.style.display === 'none' ? 'block' : 'none';
      });
      overlayEl.appendChild(modeRulesEl);
    }
  }

  document.body.appendChild(overlayEl);
}

export function updateDeployOverlay(teamName: string, teamColor: string, planetName?: string): void {
  if (!instructionEl) return;

  if (planetName) {
    instructionEl.innerHTML = `
      <div style="color: ${teamColor}; font-size: 14px; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 2px;">Deploy Phase</div>
      <div>Deploying <span style="color: ${teamColor}; font-weight: bold;">${teamName}</span> to <span style="color: ${theme.text.white};">${planetName}</span></div>
      <div style="font-size: 12px; color: ${theme.text.secondary}; margin-top: 8px;">Click a spot on the surface to set landing zone</div>
    `;
  } else {
    instructionEl.innerHTML = `
      <div style="color: ${teamColor}; font-size: 14px; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 2px;">Deploy Phase</div>
      <div>Select a planet for <span style="color: ${teamColor}; font-weight: bold;">${teamName}</span></div>
      <div style="font-size: 12px; color: ${theme.text.secondary}; margin-top: 8px;">Click a planet to select, then click a spot on its surface to confirm</div>
    `;
  }
}

export function showDeployConfirmButton(callback: () => void): void {
  if (confirmBtn) {
    confirmBtn.style.display = 'block';
    onConfirmCallback = callback;
  }
}

export function hideDeployConfirmButton(): void {
  if (confirmBtn) {
    confirmBtn.style.display = 'none';
    onConfirmCallback = null;
  }
}

export function showDeployAnimating(): void {
  if (!instructionEl) return;
  instructionEl.innerHTML = `
    <div style="color: ${theme.info}; font-size: 14px; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 2px;">Deploy Phase</div>
    <div>${fx('Deploying...', 'Drop cans inbound...')}</div>
  `;
  hideDeployConfirmButton();
}

export function hideDeployOverlay(): void {
  if (overlayEl) {
    overlayEl.remove();
    overlayEl = null;
    instructionEl = null;
    confirmBtn = null;
    onConfirmCallback = null;
    modeRulesEl = null;
  }
}
